/**
 * @fileoverview Autoplay fallback for video rendered without <deferred-media>.
 *
 * A video that renders inline (use_deferred_media: false) has no component
 * managing it, so nothing detects OS-blocked autoplay and nothing reveals the
 * fallback poster. This element wraps that markup and applies the same
 * detection the deferred component uses.
 *
 * @example
 * <video-autoplay-media>
 *   <img class="video__poster video__autoplay-poster" src="…" alt="">
 *   <video class="video__element" autoplay muted playsinline src="…"></video>
 * </video-autoplay-media>
 */

import { observeAutoplay, prepareAutoplayVideo } from '@utilities/video-autoplay';

export class VideoAutoplayMedia extends HTMLElement {
  #abortController = new AbortController();

  connectedCallback() {
    const video = this.querySelector('video');
    // An external-video iframe has no autoplay state we can read, so there is
    // nothing to detect and nothing to fall back to.
    if (!video) return;

    prepareAutoplayVideo(video, video.hasAttribute('autoplay'));

    observeAutoplay({
      video,
      host: this,
      signal: this.#abortController.signal
    });
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }
}

if (!customElements.get('video-autoplay-media')) {
  customElements.define('video-autoplay-media', VideoAutoplayMedia);
}
